import { compact, uniq } from 'es-toolkit/compat';

import { LayerVisualizationSettings } from 'components/Procedure/Infobar/slidesVisualizationAndConfiguration';
import { TableColumn } from 'interfaces/agentSession';
import { Slide } from 'interfaces/slide';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';

export interface StudyReportViewerLink {
  href: string;
  label?: string;
  actionLabel?: string;
  previewSlide?: Pick<Slide, 'id' | 'labId'>;
}

export interface StudyReportViewerLinkResolver {
  resolveCellLink: (params: {
    column: TableColumn;
    field: string;
    row: Record<string, unknown>;
    value: unknown;
    formattedValue?: unknown;
  }) => StudyReportViewerLink | null;
}

export interface StudyReportViewerLayerSettingEntry {
  layerUrlKey: string;
  settings: Partial<LayerVisualizationSettings>;
}

export const layerSettingsPerStainAndDisplayNameQueryParam = 'layerSettingsPerStainAndDisplayName';

export const addSearchParamsToUrl = (baseUrl: string, params: Record<string, string | null | undefined>) => {
  const [pathname, existingSearch = ''] = baseUrl.split('?');
  const searchParams = new URLSearchParams(existingSearch);

  Object.entries(params).forEach(([paramKey, paramValue]) => {
    if (paramValue) {
      searchParams.set(paramKey, paramValue);
    }
  });

  const encodedSearchParams = searchParams.toString();
  return encodedSearchParams ? `${pathname}?${encodedSearchParams}` : pathname;
};

export const toHashRouterUrl = (url: string) => {
  if (!url) {
    return url;
  }

  if (url.startsWith('/#') || url.startsWith('#')) {
    return url;
  }

  return `/#${url}`;
};

export const encodeStudyReportViewerMarkerChannelToggle = ({
  visibleMarkerIds,
  toggleMarkerIds,
  viewerIndex = 0,
}: {
  visibleMarkerIds: string[];
  toggleMarkerIds: string[];
  viewerIndex?: number;
}) => {
  const uniqueVisibleMarkerIds = uniq(compact(visibleMarkerIds));
  const uniqueToggleMarkerIds = uniq(compact(toggleMarkerIds));

  if (!uniqueVisibleMarkerIds.length || !uniqueToggleMarkerIds.length) {
    return null;
  }

  const encodedMarkerChannelToggle = CompressedJsonParam.encode(
    uniqueToggleMarkerIds.reduce<Record<string, Record<number, boolean>>>((markerToggles, markerId) => {
      markerToggles[markerId] = {
        [viewerIndex]: uniqueVisibleMarkerIds.includes(markerId),
      };

      return markerToggles;
    }, {})
  );

  return Array.isArray(encodedMarkerChannelToggle) ? encodedMarkerChannelToggle[0] || null : encodedMarkerChannelToggle;
};

export const encodeStudyReportViewerLayerSettings = ({
  stainTypeId,
  layerSettings,
  viewerIndex = 0,
}: {
  stainTypeId: string;
  layerSettings: StudyReportViewerLayerSettingEntry[];
  viewerIndex?: number;
}) => {
  if (!stainTypeId || !layerSettings.length) {
    return null;
  }

  const encodedLayerSettings = CompressedJsonParam.encode(
    layerSettings.reduce<Record<string, Record<string, Record<number, Partial<LayerVisualizationSettings>>>>>(
      (currentSettings, { layerUrlKey, settings }) => {
        if (!layerUrlKey) {
          return currentSettings;
        }

        currentSettings[layerUrlKey] = {
          ...(currentSettings[layerUrlKey] || {}),
          [stainTypeId]: {
            ...(currentSettings[layerUrlKey]?.[stainTypeId] || {}),
            [viewerIndex]: {
              ...(currentSettings[layerUrlKey]?.[stainTypeId]?.[viewerIndex] || {}),
              ...settings,
            },
          },
        };

        return currentSettings;
      },
      {}
    )
  );

  return Array.isArray(encodedLayerSettings) ? encodedLayerSettings[0] || null : encodedLayerSettings;
};

export const buildStudyReportViewerUrl = ({
  baseViewerUrl,
  markerChannelToggle,
  layerSettingsPerStainAndDisplayName,
  additionalSearchParams,
}: {
  baseViewerUrl: string;
  markerChannelToggle?: string | null;
  layerSettingsPerStainAndDisplayName?: string | null;
  additionalSearchParams?: Record<string, string | null | undefined>;
}) => {
  return toHashRouterUrl(
    addSearchParamsToUrl(baseViewerUrl, {
      ...(markerChannelToggle ? { markerChannelToggle } : {}),
      ...(layerSettingsPerStainAndDisplayName
        ? { [layerSettingsPerStainAndDisplayNameQueryParam]: layerSettingsPerStainAndDisplayName }
        : {}),
      ...(additionalSearchParams || {}),
    })
  );
};
